import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Pause, Play, Trash2, Eye, Target, MoreHorizontal, Filter, Sparkles, ImageIcon, Store, LayoutGrid, Bell, UserCheck, Lock, ChevronRight, Send } from 'lucide-react';
import { useCanAccessAds } from '@/lib/subscription/hooks';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/molecules/tooltip';

import { useCampaigns } from '../hooks/use-campaigns';
import { useCampaignStatus } from '../hooks/use-campaign-status';
import { useDeleteCampaign } from '../hooks/use-delete-campaign';
import { CampaignFiltersSheet, CampaignFilterButton } from '../components/filters/CampaignFiltersSheet';
import type { Campaign, CampaignStatus, AdType } from '../types/ads.types';

import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { SearchBar } from '@/components/molecules/search-bar';
import { Tabs, TabsList, TabsTrigger } from '@/components/molecules/tabs';
import { EmptyState } from '@/components/organisms/empty-state';
import { ResponsiveDataList, ColumnDef } from '@/components/organisms/responsive-data-list';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/molecules/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/molecules/alert-dialog';
import { formatPrice, getAmountByCurrency, cn } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';

const CAMPAIGN_STATUSES = ['all', 'active', 'paused', 'completed', 'draft'] as const;
const CAMPAIGN_TYPES = ['all', 'sponsored_product', 'homepage_carousel', 'featured_shop', 'category_banner', 'push_all_users', 'push_shop_subscribers'] as const;

const statusConfig: Record<CampaignStatus | 'all', { variant: 'default' | 'secondary' | 'outline' | 'destructive'; color?: string }> = {
  all: { variant: 'outline' },
  active: { variant: 'default', color: 'bg-green-500' },
  paused: { variant: 'secondary', color: 'bg-yellow-500' },
  completed: { variant: 'outline', color: 'bg-blue-500' },
  draft: { variant: 'outline', color: 'bg-gray-400' },
  disabled_by_admin: { variant: 'destructive', color: 'bg-red-500' },
  deleted_by_admin: { variant: 'destructive', color: 'bg-red-500' },
};

const typeConfig: Record<AdType, { label: string; icon: typeof Sparkles; category: 'visibility' | 'notification' }> = {
  sponsored_product: { label: 'ads.types.sponsored_product', icon: Sparkles, category: 'visibility' },
  homepage_carousel: { label: 'ads.types.homepage_carousel', icon: ImageIcon, category: 'visibility' },
  featured_shop: { label: 'ads.types.featured_shop', icon: Store, category: 'visibility' },
  category_banner: { label: 'ads.types.category_banner', icon: LayoutGrid, category: 'visibility' },
  push_all_users: { label: 'ads.types.push_all_users', icon: Bell, category: 'notification' },
  push_shop_subscribers: { label: 'ads.types.push_shop_subscribers', icon: UserCheck, category: 'notification' },
  push_product_likers: { label: 'ads.types.push_product_likers', icon: Bell, category: 'notification' },
  push_single_product_likers: { label: 'ads.types.push_single_product_likers', icon: Bell, category: 'notification' },
};

export function CampaignsListContainer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useConfigStore();
  const canAccessAds = useCanAccessAds();
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState<string>('all');
  const [type, setType] = React.useState<string>('all');
  const [deleteId, setDeleteId] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);

  const activeFiltersCount = (status !== 'all' ? 1 : 0) + (type !== 'all' ? 1 : 0);

  const { data: campaignsData, isLoading, error } = useCampaigns({
    search: search || undefined,
    status: status === 'all' ? undefined : (status as CampaignStatus),
    type: type === 'all' ? undefined : (type as AdType),
    page,
    limit: pageSize,
  });

  const { mutate: updateStatus, isPending: isUpdatingStatus } = useCampaignStatus();
  const { mutate: deleteCampaign, isPending: isDeleting } = useDeleteCampaign();

  const campaigns = campaignsData?.campaigns ?? [];
  const totalPages = campaignsData?.totalPages ?? 1;

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setPage(1);
  }, [search, status, type]);

  const handleRowClick = (campaign: Campaign) => {
    navigate(`/ads/campaigns/${campaign.refId}`);
  };

  const handleToggleStatus = (campaign: Campaign, e: React.MouseEvent) => {
    e.stopPropagation();
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    updateStatus({ refId: campaign.refId, status: newStatus });
  };

  const handleDelete = () => {
    if (deleteId) {
      deleteCampaign(deleteId, {
        onSuccess: () => setDeleteId(null),
      });
    }
  };

  // Define table columns for desktop view
  const columns: ColumnDef<Campaign>[] = React.useMemo(
    () => [
      {
        id: 'name',
        header: t('ads.campaigns.name'),
        cell: (campaign) => {
          const Icon = typeConfig[campaign.type].icon;
          return (
            <div className="flex items-center gap-3">
              <div className={cn(
                'size-9 rounded-lg flex items-center justify-center shrink-0',
                campaign.status === 'active' ? 'bg-primary/10' : 'bg-muted'
              )}>
                <Icon className={cn('size-4', campaign.status === 'active' ? 'text-primary' : 'text-muted-foreground')} />
              </div>
              <div className="min-w-0">
                <p className="font-medium truncate">{campaign.name}</p>
                <p className="text-xs text-muted-foreground">{t(typeConfig[campaign.type].label)}</p>
              </div>
            </div>
          );
        },
        sortable: true,
        className: 'min-w-[200px]',
      },
      {
        id: 'budget',
        header: t('ads.budget'),
        cell: (campaign) => {
          const spent = getAmountByCurrency(currency, { eur: campaign.spentEur, usd: campaign.spentUsd, kmf: campaign.spentKmf });
          const budget = getAmountByCurrency(currency, { eur: campaign.budgetEur, usd: campaign.budgetUsd, kmf: campaign.budgetKmf });
          const progress = budget > 0 ? (spent / budget) * 100 : 0;
          const isLow = progress > 80;
          return (
            <div className="space-y-1.5 min-w-[140px]">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{formatPrice(spent, currency)}</span>
                <span className="text-muted-foreground">/ {formatPrice(budget, currency)}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    isLow ? 'bg-orange-500' : 'bg-primary'
                  )}
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">{progress.toFixed(0)}% {t('ads.campaigns.used', 'utilisé')}</span>
            </div>
          );
        },
        className: 'min-w-[160px]',
      },
      {
        id: 'impressions',
        header: t('ads.impressions'),
        cell: (campaign) => {
          const isNotification = typeConfig[campaign.type]?.category === 'notification';
          return (
            <div className="text-center">
              <span className="font-medium tabular-nums">{(campaign.impressions ?? 0).toLocaleString()}</span>
              {isNotification && (
                <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5 mt-0.5">
                  <Send className="size-2.5" />
                  envoyées
                </p>
              )}
            </div>
          );
        },
        sortable: true,
        className: 'text-center',
      },
      {
        id: 'clicks',
        header: t('ads.clicks'),
        cell: (campaign) => {
          const isNotification = typeConfig[campaign.type]?.category === 'notification';
          return (
            <div className="text-center">
              <span className="font-medium tabular-nums">{(campaign.clicks ?? 0).toLocaleString()}</span>
              {isNotification && (
                <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5 mt-0.5">
                  <Eye className="size-2.5" />
                  ouvertes
                </p>
              )}
            </div>
          );
        },
        sortable: true,
        className: 'text-center',
      },
      {
        id: 'ctr',
        header: t('ads.ctr'),
        cell: (campaign) => {
          const ctr = campaign.ctr ?? 0;
          const isGood = ctr >= 2;
          const isNotification = typeConfig[campaign.type]?.category === 'notification';
          return (
            <div className="text-center">
              <span className={cn(
                'font-medium tabular-nums',
                isGood ? 'text-green-600' : ctr < 1 ? 'text-orange-500' : ''
              )}>
                {ctr.toFixed(2)}%
              </span>
              {isNotification && (
                <p className="text-[10px] text-muted-foreground mt-0.5">taux d'ouverture</p>
              )}
            </div>
          );
        },
        sortable: true,
        className: 'text-center',
      },
      {
        id: 'status',
        header: t('common.status'),
        cell: (campaign) => (
          <div className="flex items-center gap-2">
            <span className={cn('size-2 rounded-full', statusConfig[campaign.status].color)} />
            <Badge variant={statusConfig[campaign.status].variant} className="font-normal">
              {t(`ads.status.${campaign.status}`)}
            </Badge>
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (campaign) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/ads/campaigns/${campaign.refId}`); }}>
                <Eye className="size-4 mr-2" />
                {t('common.view')}
              </DropdownMenuItem>
              {/* Activate button for DRAFT campaigns */}
              {campaign.status === 'draft' && (
                <DropdownMenuItem onClick={(e) => handleToggleStatus(campaign, e)} disabled={isUpdatingStatus}>
                  <Play className="size-4 mr-2" />
                  {t('ads.campaigns.activate')}
                </DropdownMenuItem>
              )}
              {/* Pause/Resume for active/paused campaigns */}
              {(campaign.status === 'active' || campaign.status === 'paused') && (
                <DropdownMenuItem onClick={(e) => handleToggleStatus(campaign, e)} disabled={isUpdatingStatus}>
                  {campaign.status === 'active' ? (
                    <>
                      <Pause className="size-4 mr-2" />
                      {t('ads.campaigns.pause')}
                    </>
                  ) : (
                    <>
                      <Play className="size-4 mr-2" />
                      {t('ads.campaigns.resume')}
                    </>
                  )}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDeleteId(campaign.refId); }} className="text-destructive focus:text-destructive">
                <Trash2 className="size-4 mr-2" />
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        className: 'w-12',
      },
    ],
    [t, currency, navigate, isUpdatingStatus]
  );

  // Card renderer for mobile view - compact version
  const renderCard = (campaign: Campaign, index: number) => {
    const spent = getAmountByCurrency(currency, { eur: campaign.spentEur, usd: campaign.spentUsd, kmf: campaign.spentKmf });
    const budget = getAmountByCurrency(currency, { eur: campaign.budgetEur, usd: campaign.budgetUsd, kmf: campaign.budgetKmf });
    const progress = budget > 0 ? (spent / budget) * 100 : 0;
    const isLow = progress > 80;
    const Icon = typeConfig[campaign.type].icon;
    const isNotification = typeConfig[campaign.type]?.category === 'notification';

    return (
      <Card
        key={campaign.refId || `campaign-${index}`}
        className="overflow-hidden active:bg-muted/50 transition-colors cursor-pointer"
        onClick={() => handleRowClick(campaign)}
      >
        {/* Status bar */}
        <div className={cn('h-0.5', statusConfig[campaign.status].color)} />

        <div className="p-3 space-y-3">
          {/* Header - compact */}
          <div className="flex items-center gap-2.5">
            <div className={cn(
              'size-9 rounded-lg flex items-center justify-center shrink-0',
              campaign.status === 'active' ? 'bg-primary/10' : 'bg-muted',
              isNotification && campaign.status === 'active' && 'bg-green-500/10'
            )}>
              <Icon className={cn(
                'size-4',
                campaign.status === 'active' ? 'text-primary' : 'text-muted-foreground',
                isNotification && campaign.status === 'active' && 'text-green-600'
              )} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-sm line-clamp-1">{campaign.name}</h3>
                <ChevronRight className="size-4 text-muted-foreground shrink-0" />
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={cn('size-1.5 rounded-full', statusConfig[campaign.status].color)} />
                <span className="text-xs text-muted-foreground">
                  {t(`ads.status.${campaign.status}`)}
                </span>
                {/* Show notification badge */}
                {isNotification && (
                  <Badge variant="secondary" className="text-[9px] h-4 px-1.5">
                    <Bell className="size-2.5 mr-0.5" />
                    Push
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Budget Progress - inline */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn('h-full rounded-full transition-all', isLow ? 'bg-orange-500' : 'bg-primary')}
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
            </div>
            <span className="text-xs font-medium tabular-nums shrink-0">
              {formatPrice(spent, currency)} <span className="text-muted-foreground">/ {formatPrice(budget, currency)}</span>
            </span>
          </div>

          {/* Metrics - show different metrics for notifications */}
          {isNotification ? (
            <div className="flex gap-4 text-center">
              <div>
                <p className="font-semibold text-sm tabular-nums">{(campaign.impressions ?? 0).toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5">
                  <Send className="size-2.5" />
                  Envoyées
                </p>
              </div>
              <div>
                <p className="font-semibold text-sm tabular-nums">{(campaign.clicks ?? 0).toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5">
                  <Eye className="size-2.5" />
                  Ouvertes
                </p>
              </div>
              <div>
                <p className="font-semibold text-sm tabular-nums">{(campaign.ctr ?? 0).toFixed(1)}%</p>
                <p className="text-[10px] text-muted-foreground">Taux</p>
              </div>
            </div>
          ) : (
            <div className="flex gap-4 text-center">
              <div>
                <p className="font-semibold text-sm tabular-nums">{(campaign.impressions ?? 0).toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">{t('ads.impressions')}</p>
              </div>
              <div>
                <p className="font-semibold text-sm tabular-nums">{(campaign.clicks ?? 0).toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">{t('ads.clicks')}</p>
              </div>
              <div>
                <p className="font-semibold text-sm tabular-nums">{(campaign.ctr ?? 0).toFixed(1)}%</p>
                <p className="text-[10px] text-muted-foreground">{t('ads.ctr')}</p>
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  // Card skeleton for loading state
  const renderCardSkeleton = () => (
    <Card className="p-4">
      <div className="space-y-3">
        <div className="flex justify-between">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-20" />
        </div>
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </Card>
  );

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('ads.error')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with title and action */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">{t('ads.campaigns.title', 'Campagnes')}</h1>
          <p className="text-sm text-muted-foreground hidden sm:block">{t('ads.campaigns.subtitle', 'Gérez vos campagnes publicitaires')}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Mobile filter button */}
          <div className="sm:hidden">
            <CampaignFilterButton onClick={() => setIsFiltersOpen(true)} activeCount={activeFiltersCount} />
          </div>
          {canAccessAds ? (
            <Button asChild size="sm" className="sm:size-auto sm:px-4">
              <Link to="/ads/campaigns/new">
                <Plus className="size-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('ads.newCampaign')}</span>
              </Link>
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" className="cursor-not-allowed" disabled>
                  <Lock className="size-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('ads.newCampaign')}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('subscription.upgradeRequired', 'Abonnement Medium ou supérieur requis')}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Mobile Filters Bottom Sheet */}
      <CampaignFiltersSheet
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        status={status}
        onStatusChange={setStatus}
        type={type}
        onTypeChange={setType}
      />

      {/* Mobile: Search bar only */}
      <div className="sm:hidden">
        <SearchBar
          onSearch={setSearch}
          placeholder={t('ads.campaigns.searchPlaceholder')}
          className="w-full"
        />
      </div>

      {/* Desktop: Filters Card */}
      <Card className="shadow-sm hidden sm:block">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            {/* Left: Search and Type Filter */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <SearchBar
                onSearch={setSearch}
                placeholder={t('ads.campaigns.searchPlaceholder')}
                className="w-full sm:w-64"
              />
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="w-full sm:w-56">
                  <Filter className="size-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder={t('ads.filterByType', 'Type de campagne')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <span className="flex items-center gap-2">
                      <Target className="size-4" />
                      {t('common.all', 'Tous les types')}
                    </span>
                  </SelectItem>
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">{t('ads.visibility', 'Visibilité')}</div>
                  {CAMPAIGN_TYPES.filter(t => t !== 'all' && typeConfig[t as AdType]?.category === 'visibility').map((typeValue) => {
                    const config = typeConfig[typeValue as AdType];
                    const Icon = config.icon;
                    return (
                      <SelectItem key={typeValue} value={typeValue}>
                        <span className="flex items-center gap-2">
                          <Icon className="size-4" />
                          {t(config.label)}
                        </span>
                      </SelectItem>
                    );
                  })}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">{t('ads.notifications', 'Notifications')}</div>
                  {CAMPAIGN_TYPES.filter(t => t !== 'all' && typeConfig[t as AdType]?.category === 'notification').map((typeValue) => {
                    const config = typeConfig[typeValue as AdType];
                    const Icon = config.icon;
                    return (
                      <SelectItem key={typeValue} value={typeValue}>
                        <span className="flex items-center gap-2">
                          <Icon className="size-4" />
                          {t(config.label)}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Right: Status Filters - Wrap on mobile */}
            <div className="w-full lg:w-auto">
              <Tabs value={status} onValueChange={setStatus}>
                <TabsList className="h-auto flex-wrap gap-1.5 p-1.5 bg-muted/50">
                  {CAMPAIGN_STATUSES.map((s) => (
                    <TabsTrigger key={s} value={s} className="h-8 px-3 gap-1.5 text-xs sm:text-sm data-[state=active]:bg-background">
                      {s !== 'all' && (
                        <span className={cn('size-1.5 sm:size-2 rounded-full', statusConfig[s].color)} />
                      )}
                      {s === 'all' ? t('common.all') : t(`ads.status.${s}`)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign List */}
      <ResponsiveDataList
        data={campaigns}
        columns={columns}
        renderCard={renderCard}
        renderCardSkeleton={renderCardSkeleton}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        keyExtractor={(campaign, index) => campaign.refId || `campaign-${index}`}
        cardGridClassName="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        emptyState={
          <EmptyState
            icon={Target}
            title={t('ads.campaigns.emptyTitle')}
            description={t('ads.campaigns.emptyDescription')}
            action={
              canAccessAds ? (
                <Button asChild>
                  <Link to="/ads/campaigns/new">
                    <Plus className="size-4 mr-2" />
                    {t('ads.newCampaign')}
                  </Link>
                </Button>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button disabled className="cursor-not-allowed">
                      <Lock className="size-4 mr-2" />
                      {t('ads.newCampaign')}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('subscription.upgradeRequired', 'Abonnement Medium ou supérieur requis')}</p>
                  </TooltipContent>
                </Tooltip>
              )
            }
          />
        }
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('ads.campaigns.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('ads.campaigns.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('common.loading') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
